import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  signal,
} from '@angular/core';
import {
  AbstractControl,
  FormBuilder,
  ReactiveFormsModule,
  Validators,
  ValidationErrors,
} from '@angular/forms';
import { Router } from '@angular/router';
import { finalize } from 'rxjs';

import { ClientsService } from '../../services/clients.service';
import {
  CLIENT_ETHNICITY_2020_OPTIONS,
  CLIENT_GENDER_OPTIONS,
  CLIENT_RACE_2020_OPTIONS,
  ClientEthnicity2020,
  ClientGender,
  ClientLookupMatch,
  ClientRace2020,
  CreateClientRequest,
} from '../../models/client.models';

type InitialContactStep = 'lookup' | 'match-found' | 'create-profile';
type RequiredProfileControlName =
  | 'clientUniqueId'
  | 'enrollmentDate'
  | 'firstName'
  | 'lastName'
  | 'birthdate'
  | 'reasonForInitialContact'
  | 'race2020'
  | 'ethnicity2020'
  | 'gender'
  | 'zipCode'
  | 'consentOnFileToReferOut';

function noWhitespaceValidator(
  control: AbstractControl,
): ValidationErrors | null {
  const value = String(control.value ?? '').trim();
  return value.length ? null : { whitespace: true };
}

@Component({
  selector: 'app-initial-contact',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule],
  templateUrl: './initial-contact.component.html',
  styleUrl: './initial-contact.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class InitialContactComponent {
  private readonly requiredProfileFields: ReadonlyArray<{
    controlName: RequiredProfileControlName;
    label: string;
  }> = [
    { controlName: 'clientUniqueId', label: 'Client Unique ID' },
    { controlName: 'enrollmentDate', label: 'Enrollment Date' },
    { controlName: 'firstName', label: 'First Name' },
    { controlName: 'lastName', label: 'Last Name' },
    { controlName: 'birthdate', label: 'Birthdate' },
    { controlName: 'reasonForInitialContact', label: 'Reason for Initial Contact' },
    { controlName: 'race2020', label: 'Race' },
    { controlName: 'ethnicity2020', label: 'Ethnicity' },
    { controlName: 'gender', label: 'Gender' },
    { controlName: 'zipCode', label: 'Zip Code' },
    { controlName: 'consentOnFileToReferOut', label: 'Consent on File to Refer-Out' },
  ];

  private readonly fb = inject(FormBuilder);
  private readonly router = inject(Router);
  private readonly clientsService = inject(ClientsService);

  readonly step = signal<InitialContactStep>('lookup');

  readonly searching = signal(false);
  readonly creating = signal(false);

  readonly lookupError = signal<string | null>(null);
  readonly createError = signal<string | null>(null);
  readonly noClientFoundMessage = signal<string | null>(null);

  readonly matches = signal<ClientLookupMatch[]>([]);
  readonly genderOptions = CLIENT_GENDER_OPTIONS;
  readonly raceOptions = CLIENT_RACE_2020_OPTIONS;
  readonly ethnicityOptions = CLIENT_ETHNICITY_2020_OPTIONS;

  readonly selectedLookupSummary = computed(() => {
    const { lastName, birthdate } = this.lookupForm.getRawValue();
    return {
      lastName: lastName.trim(),
      birthdate,
    };
  });

  readonly lookupForm = this.fb.nonNullable.group({
    lastName: ['', [Validators.required, noWhitespaceValidator, Validators.maxLength(100)]],
    birthdate: ['', [Validators.required]],
  });

  readonly profileForm = this.fb.nonNullable.group({
    clientUniqueId: ['', [Validators.required, noWhitespaceValidator, Validators.maxLength(64)]],
    enrollmentDate: ['', [Validators.required]],
    firstName: ['', [Validators.required, noWhitespaceValidator, Validators.maxLength(100)]],
    middleName: ['', [Validators.maxLength(100)]],
    lastName: ['', [Validators.required, noWhitespaceValidator, Validators.maxLength(100)]],
    birthdate: ['', [Validators.required]],
    reasonForInitialContact: ['', [Validators.required, noWhitespaceValidator, Validators.maxLength(500)]],

    phone: ['', [Validators.maxLength(30)]],
    email: ['', [Validators.email, Validators.maxLength(255)]],
    race2020: ['', [Validators.required]],
    ethnicity2020: ['', [Validators.required]],
    gender: ['', [Validators.required]],
    genderOtherText: ['', [Validators.maxLength(120)]],
    zipCode: ['', [Validators.required, noWhitespaceValidator, Validators.maxLength(20)]],
    consentOnFileToReferOut: ['', [Validators.required]],
    consentOnFileToReferOutDate: [''],
  });

  submitLookup(): void {
    if (this.lookupForm.invalid || this.searching()) {
      this.lookupForm.markAllAsTouched();
      return;
    }

    const payload = {
      lastName: this.lookupForm.controls.lastName.getRawValue().trim(),
      birthdate: this.lookupForm.controls.birthdate.getRawValue(),
    };

    this.searching.set(true);
    this.lookupError.set(null);
    this.noClientFoundMessage.set(null);
    this.matches.set([]);

    this.clientsService
      .searchInitialContact(payload)
      .pipe(finalize(() => this.searching.set(false)))
      .subscribe({
        next: (response) => {
          const matches = response.matches ?? [];
          this.matches.set(matches);

          this.seedProfileFormFromLookup();

          if (matches.length > 0) {
            this.noClientFoundMessage.set(null);
            this.step.set('match-found');
          } else {
            this.noClientFoundMessage.set(
              `No client was found for last name ${payload.lastName} and birthdate ${payload.birthdate}.`,
            );
            this.step.set('create-profile');
          }
        },
        error: (error) => {
          this.lookupError.set(
            error?.error?.message ?? 'Unable to search for existing clients right now.',
          );
        },
      });
  }

  continueToCreateProfile(): void {
    this.seedProfileFormFromLookup();
    this.step.set('create-profile');
    this.createError.set(null);
    this.noClientFoundMessage.set(null);
  }

  openExistingClient(clientId: number): void {
    void this.router.navigate(['/clients', clientId]);
  }

  submitCreate(): void {
    if (this.creating()) {
      return;
    }

    this.createError.set(null);
    this.profileForm.markAllAsTouched();

    const conditionalFieldsValid = this.validateConditionalFields();
    if (this.profileForm.invalid || !conditionalFieldsValid) {
      if (!this.createError()) {
        const missingRequiredFields = this.getMissingRequiredProfileFieldLabels();
        if (missingRequiredFields.length) {
          this.createError.set(
            `Please complete the required fields: ${missingRequiredFields.join(', ')}.`,
          );
        } else {
          this.createError.set('Please correct the highlighted fields before creating the client.');
        }
      }
      return;
    }

    this.creating.set(true);

    const raw = this.profileForm.getRawValue();
    const consentOnFileToReferOut = raw.consentOnFileToReferOut === 'yes';
    const gender = this.toGenderOrNull(raw.gender);

    if (!gender) {
      this.createError.set('Please select a valid gender option.');
      return;
    }

    const race2020 = this.toRace2020OrNull(raw.race2020);
    if (!race2020) {
      this.createError.set('Please select a valid race option.');
      return;
    }

    const ethnicity2020 = this.toEthnicity2020OrNull(raw.ethnicity2020);
    if (!ethnicity2020) {
      this.createError.set('Please select a valid ethnicity option.');
      return;
    }

    const payload: CreateClientRequest = {
      clientUniqueId: raw.clientUniqueId.trim(),
      firstName: raw.firstName.trim(),
      middleName: this.nullIfBlank(raw.middleName),
      lastName: raw.lastName.trim(),
      birthdate: raw.birthdate,
      reasonForInitialContact: raw.reasonForInitialContact.trim(),

      phone: this.nullIfBlank(raw.phone),
      email: this.nullIfBlank(raw.email),
      race2020,
      ethnicity2020,
      gender,
      genderOtherText: gender === 'Other' ? this.nullIfBlank(raw.genderOtherText) : null,
      zipCode: raw.zipCode.trim(),
      consentOnFileToReferOut,
      consentOnFileToReferOutDate: consentOnFileToReferOut
        ? raw.consentOnFileToReferOutDate || null
        : null,
    };

    this.clientsService
      .createClient(payload)
      .pipe(finalize(() => this.creating.set(false)))
      .subscribe({
        next: (response) => {
          const clientId = response.client.id;
          void this.router.navigate(['/clients', clientId]);
        },
        error: (error) => {
          this.createError.set(
            error?.error?.message ?? 'Unable to create client right now.',
          );
        },
      });
  }

  goBackToLookup(): void {
    this.step.set('lookup');
    this.lookupError.set(null);
    this.noClientFoundMessage.set(null);
  }

  private seedProfileFormFromLookup(): void {
    const { lastName, birthdate } = this.lookupForm.getRawValue();

    if (!this.profileForm.controls.clientUniqueId.getRawValue().trim()) {
      this.profileForm.controls.clientUniqueId.setValue(this.generateClientUniqueId());
    }

    this.profileForm.patchValue({
      enrollmentDate: this.todayDateString(),
      lastName: lastName.trim(),
      birthdate,
    });
  }

  private nullIfBlank(value: string): string | null {
    const trimmed = value.trim();
    return trimmed ? trimmed : null;
  }

  private toGenderOrNull(value: string): ClientGender | null {
    const trimmed = value.trim();
    if (!trimmed) {
      return null;
    }

    return this.genderOptions.includes(trimmed as ClientGender)
      ? (trimmed as ClientGender)
      : null;
  }

  private toRace2020OrNull(value: string): ClientRace2020 | null {
    const trimmed = value.trim();
    if (!trimmed) {
      return null;
    }

    return this.raceOptions.includes(trimmed as ClientRace2020)
      ? (trimmed as ClientRace2020)
      : null;
  }

  private toEthnicity2020OrNull(value: string): ClientEthnicity2020 | null {
    const trimmed = value.trim();
    if (!trimmed) {
      return null;
    }

    return this.ethnicityOptions.includes(trimmed as ClientEthnicity2020)
      ? (trimmed as ClientEthnicity2020)
      : null;
  }

  private validateConditionalFields(): boolean {
    const raw = this.profileForm.getRawValue();
    const gender = raw.gender.trim();
    const genderOtherText = raw.genderOtherText.trim();
    const consentOnFileToReferOut = raw.consentOnFileToReferOut;
    const consentDate = raw.consentOnFileToReferOutDate;

    if (gender === 'Other' && !genderOtherText) {
      this.profileForm.controls.genderOtherText.setErrors({ required: true });
      this.createError.set('Please specify the gender when selecting Other.');
      return false;
    }

    if (gender !== 'Other' && genderOtherText) {
      this.profileForm.controls.genderOtherText.setErrors({ invalid: true });
      this.createError.set('Gender detail is only allowed when gender is Other.');
      return false;
    }

    if (consentOnFileToReferOut === 'yes' && !consentDate) {
      this.profileForm.controls.consentOnFileToReferOutDate.setErrors({ required: true });
      this.createError.set('Consent date is required when consent is on file.');
      return false;
    }

    this.profileForm.controls.genderOtherText.setErrors(null);
    this.profileForm.controls.consentOnFileToReferOutDate.setErrors(null);
    return true;
  }

  isRequiredFieldMissing(control: AbstractControl): boolean {
    return (
      control.touched
      && (control.hasError('required') || control.hasError('whitespace'))
    );
  }

  private getMissingRequiredProfileFieldLabels(): string[] {
    return this.requiredProfileFields
      .filter(({ controlName }) => {
        const control = this.profileForm.get(controlName);
        return !!control && (control.hasError('required') || control.hasError('whitespace'));
      })
      .map(({ label }) => label);
  }

  private todayDateString(): string {
    return new Date().toISOString().slice(0, 10);
  }

  private generateClientUniqueId(): string {
    const alphabet = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789';
    let suffix = '';

    for (let i = 0; i < 8; i += 1) {
      suffix += alphabet[Math.floor(Math.random() * alphabet.length)];
    }

    return `CL-${suffix}`;
  }
}